import { usePropertyContext } from "components/pages/Account/Property/context";
import { useMemo } from "react";
import moment from "moment";
import { LightEnrollment } from "./types";

interface LightEnrollmentResult {
	isEligibleForLightEnrollment: boolean;
	totalProjectedAnnualSavings: number;
	showProjectedSavingsBox: boolean;
}

const calculateTotalProjectedAnnualSavings = (
	lightEnrollment: LightEnrollment
): number => {
	let savingsAmount = 0;
	if (lightEnrollment?.projected_bill_data) {
		const data =
			typeof lightEnrollment.projected_bill_data === "string"
				? JSON.parse(lightEnrollment.projected_bill_data)
				: lightEnrollment.projected_bill_data;

		if (
			data?.message?.projected_monthly_bill_amounts &&
			data?.message?.projected_reduced_monthly_bill_amounts
		) {
			const projectedSum = data.message.projected_monthly_bill_amounts.reduce(
				(sum: number, amount: number) => sum + amount,
				0
			);
			const reducedSum =
				data.message.projected_reduced_monthly_bill_amounts.reduce(
					(sum: number, amount: number) => sum + amount,
					0
				);
			savingsAmount = projectedSum - reducedSum;
		}
	}
	return savingsAmount;
};

const shouldShowProjectedSavingsBox = (
	lightEnrollment: LightEnrollment
): boolean => {
	const totalProjectedAnnualSavings =
		calculateTotalProjectedAnnualSavings(lightEnrollment);

	return !!(
		lightEnrollment.status === "completed" &&
		lightEnrollment.projected_bill_data &&
		totalProjectedAnnualSavings > 10 &&
		(lightEnrollment.contract_end_date == null ||
			moment(lightEnrollment.contract_end_date).isBefore(moment()))
	);
};

export const isLightEnrollmentEligible = (
	lightEnrollment: LightEnrollment
): boolean => {
	if (!lightEnrollment) return false;

	const showProjectedSavingsBox =
		shouldShowProjectedSavingsBox(lightEnrollment);

	return !!(showProjectedSavingsBox && lightEnrollment.enrolled == false);
};

export const useIsEligibleForLightEnrollment = (): LightEnrollmentResult => {
	const { details } = usePropertyContext();

	return useMemo(() => {
		if (!details?.light_enrollment)
			return {
				isEligibleForLightEnrollment: false,
				totalProjectedAnnualSavings: 0,
				showProjectedSavingsBox: false,
			};

		const totalProjectedAnnualSavings = calculateTotalProjectedAnnualSavings(
			details.light_enrollment
		);
		const showProjectedSavingsBox = shouldShowProjectedSavingsBox(
			details.light_enrollment
		);
		const isEligibleForLightEnrollment = !!(
			showProjectedSavingsBox && details.light_enrollment.enrolled == false
		);

		return {
			isEligibleForLightEnrollment,
			totalProjectedAnnualSavings,
			showProjectedSavingsBox,
		};
	}, [details?.light_enrollment]);
};
